import SEOHead from "@/components/SEOHead";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Gift } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

interface OrderRow {
  id: string;
  created_at: string;
  status: string | null;
  amount: number | null;
  currency: string | null;
  recipient_name: string | null;
  occasion_type: string | null;
  delivery_date: string | null;
  selected_keepsake_id: string | null;
  personalisation_message: string | null;
  stripe_payment_link: string | null;
  product: {
    name: string;
    image_url: string | null;
    category: string | null;
  } | null;
}

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  initiated: { label: "Awaiting payment", className: "bg-amber-100 text-amber-800 border-amber-200" },
  completed: { label: "Order confirmed", className: "bg-green-100 text-green-800 border-green-200" },
  in_production: { label: "In production", className: "bg-blue-100 text-blue-800 border-blue-200" },
  dispatched: { label: "Dispatched", className: "bg-purple-100 text-purple-800 border-purple-200" },
  delivered: { label: "Delivered", className: "bg-accent/20 text-foreground border-accent" },
  cancelled: { label: "Cancelled", className: "bg-red-100 text-red-800 border-red-200" },
};

const OrderHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  useEffect(() => {
    const payment = searchParams.get("payment");
    if (payment === "success") {
      toast.success("Payment confirmed 🎁", {
        description: "Your gift order is confirmed. Check your email for details.",
        duration: 6000,
      });
      setSearchParams({});
    } else if (payment === "cancelled") {
      toast.error("Payment cancelled", {
        description: "Your order was not completed. You can try again any time.",
        duration: 5000,
      });
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!user) return;
    const fetchOrders = async () => {
      setLoading(true);
      setError(false);
      const { data, error: err } = await supabase
        .from("gift_orders")
        .select(`
          id,
          created_at,
          status,
          amount,
          currency,
          recipient_name,
          occasion_type,
          delivery_date,
          selected_keepsake_id,
          personalisation_message,
          stripe_payment_link,
          product:gift_products!gift_orders_product_id_fkey(name, image_url, category)
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (err) {
        console.error("Failed to fetch orders:", err);
        setError(true);
      } else {
        setOrders((data as unknown as OrderRow[]) || []);
      }
      setLoading(false);
    };
    fetchOrders();
  }, [user]);

  const handleContinuePayment = (order: OrderRow) => {
    if (order.stripe_payment_link) {
      window.location.href = order.stripe_payment_link;
    } else {
      toast.error("Can't reach Dearestly right now. Check your connection and try again.");
    }
  };

  const confirmCancel = async () => {
    if (!cancellingId) return;
    try {
      const { error } = await supabase.rpc("cancel_gift_order", { order_id: cancellingId });
      if (error) throw error;
      setOrders((prev) =>
        prev.map((o) => (o.id === cancellingId ? { ...o, status: "cancelled" } : o))
      );
      toast.success("Order cancelled.");
    } catch {
      toast.error("That didn't work — give it another go.");
    } finally {
      setCancellingId(null);
    }
  };

  const statusBadge = (status: string | null) => {
    const config = STATUS_CONFIG[status || ""] || { label: status || "Unknown", className: "bg-muted text-muted-foreground" };
    return (
      <>
        <SEOHead title="Order history — Dearestly." description="Review your Dearestly. gift order history." path="/orders" noindex />
        <Badge variant="outline" className={`text-[11px] font-medium ${config.className}`}>
        {config.label}
      </Badge>
    </>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-4 md:px-6">
          <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} aria-label="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-page-title text-foreground">Your gift orders</h1>
            <div className="mt-0.5 h-0.5 w-10 rounded-full bg-accent" />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-8 md:px-6 animate-fade-in">
        <p className="mb-6 text-muted-sm">Every gift you've sent through Dearestly</p>

        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card-premium p-5">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-14 w-14 rounded-full shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-16">
            <p className="text-body text-muted-foreground">Something went wrong loading your orders. Please try again.</p>
          </div>
        )}

        {!loading && !error && orders.length === 0 && (
          <div className="flex flex-col items-center py-20 text-center">
            <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-accent/10">
              <Gift className="h-10 w-10 text-accent" />
            </div>
            <h2 className="mb-3 text-section-heading text-foreground">
              No gifts sent yet.
            </h2>
            <p className="mb-8 max-w-xs text-body text-muted-foreground">
              When you send a Dearestly gift, it will appear here — a record of every time you showed up.
            </p>
            <Button onClick={() => navigate("/dashboard")} size="lg">
              Browse gifts →
            </Button>
          </div>
        )}

        {!loading && !error && orders.length > 0 && (
          <div className="space-y-3">
            {orders.map((order) => (
              <div
                key={order.id}
                className="card-premium p-5"
                style={{ borderLeftWidth: "3px", borderLeftColor: "hsl(var(--accent))" }}
              >
                <div className="flex items-start gap-4">
                  {order.product?.image_url ? (
                    <img
                      src={order.product.image_url}
                      alt={order.product.name}
                      className="h-14 w-14 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-accent/10">
                      <Gift className="h-6 w-6 text-accent" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-card-heading text-foreground truncate">
                      {order.product?.name || "Gift"}
                    </p>
                    {order.recipient_name && (
                      <p className="text-muted-sm truncate">
                        For {order.recipient_name}
                      </p>
                    )}
                    {order.occasion_type && (
                      <p className="text-xs text-muted-foreground capitalize">
                        {order.occasion_type}
                      </p>
                    )}
                    {order.delivery_date && (
                      <p className="text-xs text-muted-foreground">
                        Preferred delivery: {format(new Date(order.delivery_date), "dd MMM yyyy")}
                      </p>
                    )}
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {statusBadge(order.status)}
                      {order.amount != null && (
                        <span className="text-xs font-medium text-muted-foreground">
                          {order.currency?.toUpperCase() || "USD"} {Number(order.amount).toFixed(2)}
                        </span>
                      )}
                    </div>

                    {/* Action buttons for initiated orders */}
                    {order.status === "initiated" && (
                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => handleContinuePayment(order)}
                        >
                          Complete payment →
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setCancellingId(order.id)}
                        >
                          Cancel order
                        </Button>
                      </div>
                    )}

                    {/* Cancelled support text */}
                    {order.status === "cancelled" && (
                      <p className="mt-2 text-xs text-muted-foreground">
                        Order cancelled. Questions?{" "}
                        <a
                          href="mailto:hello@circle.dearestly.co"
                          className="underline hover:text-foreground transition-colors"
                        >
                          Email hello@circle.dearestly.co
                        </a>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={!!cancellingId} onOpenChange={(open) => !open && setCancellingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel your gift order. If you've already completed payment, please contact us at hello@circle.dearestly.co to arrange a refund.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep order</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCancel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrderHistory;
